import React, { FC, ReactElement, Ref, useContext, useMemo, useRef, useState } from 'react'
import { CustomiseRole as CustomiseRoleLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { FormProvider, useForm, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { IconName } from '../../Common/Components/Icon'
import PopperBubble from '../../Common/Components/PopperBubble'
import { Table, TableColumn, TableData, TableRow } from '../../Common/Components/Table/Table'
import { createHeaderRow } from '../../Common/Components/Table/TableUtils'
import Tabs from '../../Common/Components/Tabs'
import { Tab } from '../../Common/Components/Tabs/Types'
import { ON_CHANGE } from '../../Common/Constants/HookFormEventNames'
import { LanguageContext, ReactState } from '../../Common/Contexts'
import UserInfoContext, { UserInfoContextType } from '../../Common/Contexts/UserInfoContext'
import { RoleOption } from '../../Common/Types/Types'
import { ADMIN_USERNAME, CustomiseRoleTabIDs, DEFAULT_CME_ID } from '../Constants'
import {
    formToPermissionsMap,
    ROLE_COMPONENT_NAME,
    RolePermissionsFieldNames,
    RolePermissionsNameSpacing
} from '../FieldNames'
import Custom from '../Roles/Templates/Custom.json'
import { FooterActions } from './FooterActions'
import { BackendPermissionNames, ROLE_ACTIONS } from '../../Common/Constants/Permissions'
import { PermissionsUtil } from '../../Common/Utils/PermissionsUtil'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'

const CENTER_ALIGN: 'center' = 'center' as const

type RowData = {
    name: string
    checkboxesDisabled: Definable<boolean>[]
    title: string[]
    validation?: boolean
    rowClassName: string
}

type TableEntry = {
    title: string
    headings: string[]
    rows: RowData[]
}

type CustomiseForm = {
    administrator: {
        rolepermissions: Record<string, Record<string, boolean>>
    }
}

/**
 * Checks whether user has Admin permissions which allow the same access as EPT Access role.
 *
 */
const userHasEqualAdminPermissionsToEptAccess = (watch: UseFormMethods<CustomiseForm>['watch']): boolean =>
    !!(
        watch(`${RolePermissionsNameSpacing(RolePermissionsFieldNames.TELEPHONY_SERVER)}.${ROLE_ACTIONS.READ}`) &&
        watch(`${RolePermissionsNameSpacing(RolePermissionsFieldNames.PARTITIONS)}.${ROLE_ACTIONS.READ}`) &&
        watch(`${RolePermissionsNameSpacing(RolePermissionsFieldNames.AGENTS)}.${ROLE_ACTIONS.READ}`) &&
        watch(`${RolePermissionsNameSpacing(RolePermissionsFieldNames.DEVICES)}.${ROLE_ACTIONS.READ}`) &&
        watch(`${RolePermissionsNameSpacing(RolePermissionsFieldNames.APPLICATION_USER)}.${ROLE_ACTIONS.READ}`) &&
        watch(`${RolePermissionsNameSpacing(RolePermissionsFieldNames.APPLICATION_USER)}.${ROLE_ACTIONS.CREATE}`) &&
        watch(`${RolePermissionsNameSpacing(RolePermissionsFieldNames.APPLICATION_USER)}.${ROLE_ACTIONS.UPDATE}`) &&
        watch(`${RolePermissionsNameSpacing(RolePermissionsFieldNames.APPLICATION_USER)}.${ROLE_ACTIONS.DELETE}`)
    )

/**
 * Create row entry.
 *
 * @param name - The name for React Hook Form.
 * @param checkboxesDisabled
 * @param rowTitle - Title of the row (displayed in first column)
 * @param userPermissions - The user's permissions.
 */
const createRow = (
    { setValue, watch }: UseFormMethods<CustomiseForm>,
    name: string,
    checkboxesDisabled: Definable<boolean>[],
    nameArr: string[],
    userPermissions: string[],
    rowClassName: string,
    ...rowTitles: string[]
): TableRow => ({
    rowClassName,
    columns: [
        {
            content: (
                <div className='check-row-title'>
                    {rowTitles.map(
                        (rowTitle: string, index: number): ReactElement => (
                            <b key={`rowTitle-${index}`}>{rowTitle}</b>
                        )
                    )}
                </div>
            ),
            props: {
                component: 'th',
                align: CENTER_ALIGN
            }
        },
        ...checkboxesDisabled.map((checkboxDisabled: Definable<boolean>, index: number): TableColumn => {
            const language: CLanguageHandler = CLanguageHandler.getInstance()
            const [popperOpen, setPopperOpen]: ReactState<boolean> = useState<boolean>(false)
            const [selectedEditOptions, setSelectedEditOptions]: ReactState<string> = useState<string>('')
            const anchorEl: Ref<HTMLDivElement> = useRef(null)

            const action: string = nameArr[index]
            const entryName: string = `${RolePermissionsNameSpacing(name)}.${action}`
            const backendKeys: string[] = ([] as string[]).concat(formToPermissionsMap.key(name))
            const permission: boolean = backendKeys.some((backendKey: string): boolean =>
                userPermissions.includes(`${backendKey}${action !== ROLE_ACTIONS.ALLOWED ? `|${action}` : ''}`)
            )

            return {
                content:
                    checkboxDisabled !== undefined ? (
                        <div
                            className='cr-role-element-container'
                            ref={anchorEl}
                            onMouseLeave={(): void => setPopperOpen(false)}
                        >
                            {action === ROLE_ACTIONS.READ ? (
                                <PopperBubble
                                    anchorEl={anchorEl.current}
                                    content={
                                        <div className='view-change-warning'>
                                            {language.getInterpolated(
                                                CustomiseRoleLanguage.ViewChangedWarning,
                                                rowTitles[index],
                                                selectedEditOptions.toLowerCase()
                                            )}
                                        </div>
                                    }
                                    open={popperOpen}
                                    placement='top'
                                    infoMode={true}
                                />
                            ) : (
                                <></>
                            )}
                            <RouterCheckbox
                                disabled={!permission || checkboxDisabled}
                                name={entryName}
                                onChange={(newValue: boolean): void => {
                                    setValue(ROLE_COMPONENT_NAME, Custom.value)

                                    if (
                                        (action === ROLE_ACTIONS.CREATE ||
                                            action === ROLE_ACTIONS.DELETE ||
                                            action === ROLE_ACTIONS.UPDATE) &&
                                        newValue
                                    ) {
                                        setValue(`${RolePermissionsNameSpacing(name)}.${ROLE_ACTIONS.READ}`, true)
                                    } else if (action === ROLE_ACTIONS.READ && !newValue) {
                                        const isEditOptions: RoleOption[] = [
                                            {
                                                label: language.get(CustomiseRoleLanguage.CustomiseRole_Add),
                                                value: watch(
                                                    `${RolePermissionsNameSpacing(name)}.${ROLE_ACTIONS.CREATE}`
                                                )
                                            },
                                            {
                                                label: language.get(CustomiseRoleLanguage.Update),
                                                value: watch(
                                                    `${RolePermissionsNameSpacing(name)}.${ROLE_ACTIONS.UPDATE}`
                                                )
                                            },
                                            {
                                                label: language.get(CustomiseRoleLanguage.Delete),
                                                value: watch(
                                                    `${RolePermissionsNameSpacing(name)}.${ROLE_ACTIONS.DELETE}`
                                                )
                                            }
                                        ]

                                        if (isEditOptions.some((editOption: RoleOption) => editOption.value)) {
                                            const activeEditOptions: RoleOption[] = isEditOptions.filter(
                                                (editOption: RoleOption): boolean => editOption.value
                                            )
                                            setSelectedEditOptions(
                                                activeEditOptions
                                                    .map((editOption: RoleOption): string => editOption.label)
                                                    .reduce(
                                                        (
                                                            accum: string,
                                                            next: string,
                                                            activeEditsIndex: number
                                                        ): string => {
                                                            if (activeEditsIndex === 0) {
                                                                return next
                                                            }

                                                            if (activeEditsIndex === activeEditOptions.length - 1) {
                                                                return `${accum}${language.get(
                                                                    CustomiseRoleLanguage.ViewChangeOr
                                                                )}${next}`
                                                            }

                                                            return `${accum}, ${next}`
                                                        },
                                                        ''
                                                    )
                                            )

                                            setPopperOpen(true)
                                        }
                                    }

                                    if (name === RolePermissionsFieldNames.APPLICATION_USER) {
                                        if (
                                            (action === ROLE_ACTIONS.CREATE ||
                                                action === ROLE_ACTIONS.DELETE ||
                                                action === ROLE_ACTIONS.UPDATE) &&
                                            newValue
                                        ) {
                                            // Automatically grant UserRoles Read permission if Application Users permission is being granted.
                                            setValue(
                                                `${RolePermissionsNameSpacing(RolePermissionsFieldNames.USER_ROLE)}.${
                                                    ROLE_ACTIONS.READ
                                                }`,
                                                true
                                            )
                                        } else if (action === ROLE_ACTIONS.READ) {
                                            // Automatically update UserRoles Read permission if Application Users permission Read is being granted or revoked.
                                            setValue(
                                                `${RolePermissionsNameSpacing(RolePermissionsFieldNames.USER_ROLE)}.${
                                                    ROLE_ACTIONS.READ
                                                }`,
                                                newValue
                                            )
                                        }
                                    }
                                    // User is adding a permission.
                                    if (
                                        newValue &&
                                        (name === RolePermissionsFieldNames.TELEPHONY_SERVER ||
                                            name === RolePermissionsFieldNames.PARTITIONS ||
                                            name === RolePermissionsFieldNames.DEVICES ||
                                            name === RolePermissionsFieldNames.AGENTS ||
                                            name === RolePermissionsFieldNames.APPLICATION_USER) &&
                                        userHasEqualAdminPermissionsToEptAccess(watch)
                                    ) {
                                        // Admin Permissions that allow access equal to EPT Access are being added, also add EPT_ACCESS.
                                        setValue(
                                            `${RolePermissionsNameSpacing(RolePermissionsFieldNames.EPT_ACCESS)}.${
                                                ROLE_ACTIONS.ALLOWED
                                            }`,
                                            true
                                        )
                                    }
                                }}
                            />
                        </div>
                    ) : (
                        ''
                    ),
                props: { align: CENTER_ALIGN }
            }
        })
    ]
})

/**
 * Create table data entries.
 *
 * @param methods - Form methods.
 * @param title - Title for the entry.
 * @param headings - Headings for the entry.
 * @param rows - Rows for the entry.
 * @param nameArr - Array of names.
 * @param userPermissions - User permissions list.
 */
const tableDataCreator = (
    methods: UseFormMethods<CustomiseForm>,
    { title, headings, rows }: TableEntry,
    nameArr: string[],
    userPermissions: string[]
): {
    title: string
    tableData: TableData
} => ({
    title,
    tableData: {
        headings: createHeaderRow(CENTER_ALIGN, ...headings),
        rows: rows
            .filter((rowItem: RowData): boolean => (IsVarEmpty(rowItem?.validation) ? true : rowItem.validation))
            .map(
                ({ name, title: rowTitle, checkboxesDisabled, rowClassName }: RowData): TableRow =>
                    createRow(methods, name, checkboxesDisabled, nameArr, userPermissions, rowClassName, ...rowTitle)
            )
    }
})

type Props = {
    cmePermissionValue: string
    rolePermissions: Record<string, Record<string, boolean>>
    closeModal: () => void
    onSubmit: (records: Record<string, Record<string, boolean>>) => void
}

/**
 * Customise role popup contents.
 *
 * @param rolePermissions - Permissions for the role in form structure.
 * @param closeModal - Close modal function.
 * @param onSubmit - What to do when submitted.
 * @param cmePermissionValue - CM Environment ID Scope selected for user being created / edited.
 */
export const CustomiseRole: FC<Props> = ({
    rolePermissions,
    closeModal,
    onSubmit,
    cmePermissionValue
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(CustomiseRoleLanguage.NAME)

    // Active tab ID.
    const [activeTabID, setActiveTabID] = useState<number>(CustomiseRoleTabIDs.ADMINISTRATE)

    // Form context.
    const methods: UseFormMethods<CustomiseForm> = useForm({
        defaultValues: { administrator: { rolepermissions: rolePermissions } },
        mode: ON_CHANGE
    })

    const userInfo: UserInfoContextType = useContext(UserInfoContext)

    const { handleSubmit } = methods

    const hasTelsysSyncRestrictionPerms: boolean = useMemo(
        (): boolean =>
            PermissionsUtil.hasPermissions(
                userInfo,
                PermissionsUtil.builder(BackendPermissionNames.TELSYS_SYNC_RESTRICTIONS).allowed()
            ) ?? false,
        [userInfo?.Permissions?.Admin]
    )

    const adminPermissionsLayout: TableEntry = {
        title: language.get(CustomiseRoleLanguage.TableHeadingAdminAccess),
        headings: [
            language.get(CustomiseRoleLanguage.HeadingAdministrate),
            language.get(CustomiseRoleLanguage.HeadingView),
            language.get(CustomiseRoleLanguage.HeadingAdd),
            language.get(CustomiseRoleLanguage.HeadingModify),
            language.get(CustomiseRoleLanguage.HeadingDelete)
        ],
        rows: [
            {
                name: RolePermissionsFieldNames.CME_MANAGEMENT,
                checkboxesDisabled: [false, false, false, false],
                title: [language.get(CustomiseRoleLanguage.EntryCme)],
                rowClassName: 'cme-management'
            },
            {
                name: RolePermissionsFieldNames.DEVICES,
                checkboxesDisabled: [false, false, false, false],
                title: [
                    language.get(CustomiseRoleLanguage.EntryDevices),
                    language.get(CustomiseRoleLanguage.EntryDevicesSub)
                ],
                rowClassName: 'devices'
            },
            {
                name: RolePermissionsFieldNames.AGENTS,
                checkboxesDisabled: [false, false, false, false],
                title: [language.get(CustomiseRoleLanguage.EntryAgents)],
                rowClassName: 'agents'
            },
            {
                name: RolePermissionsFieldNames.DIRECTORY,
                checkboxesDisabled: [false, false, false, false],
                title: [language.get(CustomiseRoleLanguage.EntryDirectory)],
                rowClassName: 'directories'
            },
            {
                name: RolePermissionsFieldNames.APPLICATION_USER,
                checkboxesDisabled: [false, false, false, false],
                title: [language.get(CustomiseRoleLanguage.EntryApplicationUsers)],
                rowClassName: 'application-users'
            },
            {
                name: RolePermissionsFieldNames.CODES,
                checkboxesDisabled: [false, false, false, false],
                title: [language.get(CustomiseRoleLanguage.EntryCodes)],
                rowClassName: 'codes'
            },
            {
                name: RolePermissionsFieldNames.TELEPHONY_SERVER,
                checkboxesDisabled: [false, false, false, false],
                title: [language.get(CustomiseRoleLanguage.EntryTelServers)],
                rowClassName: 'telephony-servers'
            },
            {
                name: RolePermissionsFieldNames.PARTITIONS,
                checkboxesDisabled: [false, false, false, false],
                title: [language.get(CustomiseRoleLanguage.EntryPartition)],
                rowClassName: 'partitions'
            },
            {
                name: RolePermissionsFieldNames.RECORDINGS,
                checkboxesDisabled: [false, false, false, false],
                title: [language.get(CustomiseRoleLanguage.EntryRecordings)],
                rowClassName: 'recordings'
            },
            {
                name: RolePermissionsFieldNames.MESSAGES,
                checkboxesDisabled: [false, undefined, false, false],
                title: [language.get(CustomiseRoleLanguage.EntryMessages)],
                rowClassName: 'messages'
            },
            {
                name: RolePermissionsFieldNames.DATABASE,
                checkboxesDisabled: [false, undefined, false, undefined],
                title: [language.get(CustomiseRoleLanguage.EntryDatabase)],
                rowClassName: 'database'
            },
            {
                name: RolePermissionsFieldNames.USER_ROLE,
                checkboxesDisabled: [
                    methods.watch(
                        `${RolePermissionsNameSpacing(RolePermissionsFieldNames.APPLICATION_USER)}.${ROLE_ACTIONS.READ}`
                    ) || false,
                    false,
                    undefined,
                    false
                ],
                title: [language.get(CustomiseRoleLanguage.EntryUserRole)],
                rowClassName: 'userRole'
            },
            {
                name: RolePermissionsFieldNames.CRM_CONFIG,
                checkboxesDisabled: [false, false, false, false],
                title: [language.get(CustomiseRoleLanguage.EntryCrm)],
                rowClassName: 'crmConfig'
            },
            {
                name: RolePermissionsFieldNames.CLIENT_CONFIG,
                checkboxesDisabled: [false, false, false, false],
                title: [language.get(CustomiseRoleLanguage.EntryClientConfig)],
                rowClassName: 'client-config'
            },
            {
                name: RolePermissionsFieldNames.POLICY,
                checkboxesDisabled: [false, true, false, false],
                title: [language.get(CustomiseRoleLanguage.Policy)],
                rowClassName: 'policy'
            }
        ]
    }

    const statusToolsLayout: TableEntry = {
        title: language.get(CustomiseRoleLanguage.TableHeadingStatusTools),
        headings: [
            language.get(CustomiseRoleLanguage.HeadingSection),
            language.get(CustomiseRoleLanguage.HeadingView),
            language.get(CustomiseRoleLanguage.HeadingModify)
        ],
        rows: [
            {
                name: RolePermissionsFieldNames.LICENCES,
                checkboxesDisabled: [false, false],
                title: [language.get(CustomiseRoleLanguage.EntryLicences)],
                rowClassName: 'licences'
            },
            {
                name: RolePermissionsFieldNames.LOGGING,
                checkboxesDisabled: [false, undefined],
                title: [language.get(CustomiseRoleLanguage.EntryLogging)],
                rowClassName: 'logging'
            },
            {
                name: RolePermissionsFieldNames.LOG_MANAGEMENT,
                checkboxesDisabled: [false, false],
                title: [language.get(CustomiseRoleLanguage.EntryLogManagement)],
                rowClassName: 'log-management'
            },
            {
                name: RolePermissionsFieldNames.AUDIT_TRAIL,
                checkboxesDisabled: [false, undefined],
                title: [language.get(CustomiseRoleLanguage.EntryAuditTrail)],
                rowClassName: 'audit-trail'
            },
            {
                name: RolePermissionsFieldNames.TEL_MODULE_STATUS,
                checkboxesDisabled: [false, undefined],
                title: [language.get(CustomiseRoleLanguage.EntryTelephoneModuleStatus)],
                rowClassName: 'telephone-module-status'
            },
            {
                name: RolePermissionsFieldNames.CALL_CONTROL,
                checkboxesDisabled: [false, undefined],
                title: [language.get(CustomiseRoleLanguage.EntryCallControl)],
                rowClassName: 'call-control'
            },
            {
                name: RolePermissionsFieldNames.SERVER_STATUS,
                checkboxesDisabled: [false, undefined],
                title: [language.get(CustomiseRoleLanguage.EntryServerStatus)],
                rowClassName: 'server-status'
            }
        ]
    }

    const actionsLayout: TableEntry = {
        title: language.get(CustomiseRoleLanguage.TableHeadingActions),
        headings: [
            language.get(CustomiseRoleLanguage.HeadingAction),
            language.get(CustomiseRoleLanguage.HeadingAllowed)
        ],
        rows: [
            {
                name: RolePermissionsFieldNames.COPY_REPORTS_TO,
                checkboxesDisabled: [false],
                title: [language.get(CustomiseRoleLanguage.EntryCopyReportsTo)],
                rowClassName: 'copy-reports-to'
            },
            {
                name: RolePermissionsFieldNames.SIGN_IN_AS,
                checkboxesDisabled: [false],
                title: [language.get(CustomiseRoleLanguage.EntrySignInAs)],
                rowClassName: 'sign-in-as'
            },
            {
                name: RolePermissionsFieldNames.BILLING_FILES_DOWNLOAD,
                checkboxesDisabled: [false],
                title: [language.get(CustomiseRoleLanguage.EntryBillingFilesDownload)],
                rowClassName: 'billing-cdr'
            },
            {
                name: RolePermissionsFieldNames.BILLING_SUMMARY,
                checkboxesDisabled: [false],
                title: [language.get(CustomiseRoleLanguage.EntryBillingSummary)],
                rowClassName: 'billing-summary'
            },
            {
                name: RolePermissionsFieldNames.CHANGE_TELEPHONY_BILLING_STATUS,
                checkboxesDisabled: [false],
                title: [language.get(CustomiseRoleLanguage.EntryChangeTelephonyServerBillingStatus)],
                validation: userInfo?.Username === ADMIN_USERNAME,
                rowClassName: 'change-telephony-server-billing-status'
            },
            {
                name: RolePermissionsFieldNames.CHANGE_LICENCE_LEVEL,
                checkboxesDisabled: [false],
                title: [language.get(CustomiseRoleLanguage.EntryChangeLicenceLevel)],
                rowClassName: 'change-licence-level'
            },
            {
                name: RolePermissionsFieldNames.SYNC_PARTITION,
                checkboxesDisabled: [false],
                title: [language.get(CustomiseRoleLanguage.SyncPartition)],
                rowClassName: 'sync-partition'
            },
            {
                name: RolePermissionsFieldNames.EPT_ACCESS,
                checkboxesDisabled: [userHasEqualAdminPermissionsToEptAccess(methods.watch)],
                title: [language.get(CustomiseRoleLanguage.EptAccess)],
                rowClassName: 'ept-access'
            },
            {
                name: RolePermissionsFieldNames.LOG_FILES_DOWNLOAD,
                checkboxesDisabled: [false],
                title: [language.get(CustomiseRoleLanguage.EntryLogFilesDownload)],
                validation: cmePermissionValue === DEFAULT_CME_ID,
                rowClassName: 'download-log-files'
            },
            {
                name: RolePermissionsFieldNames.TELSYS_SYNC_RESTRICTIONS,
                checkboxesDisabled: [!hasTelsysSyncRestrictionPerms],
                title: [language.get(CustomiseRoleLanguage.TelSysSyncRestrictions)],
                rowClassName: 'tel-sys-sync-restrictions'
            },
            {
                name: RolePermissionsFieldNames.CME_BRANDING_CONFIG,
                checkboxesDisabled: [false],
                title: [language.get(CustomiseRoleLanguage.CMEBrandingConfig)],
                rowClassName: 'cme-branding-config'
            },
            {
                name: RolePermissionsFieldNames.EDIT_OWN_CME_DOMAIN_PROFILE,
                checkboxesDisabled: [false],
                title: [language.get(CustomiseRoleLanguage.EditOwnCMEDomainProfile)],
                rowClassName: 'cme-edit-domain-profile'
            },
            {
                name: RolePermissionsFieldNames.EDIT_OWN_CME_BRANDING,
                checkboxesDisabled: [false],
                title: [language.get(CustomiseRoleLanguage.EditOwnCMEBranding)],
                rowClassName: 'cme-edit-branding'
            },
            {
                name: RolePermissionsFieldNames.EDIT_OWN_CME_PROVISIONING,
                checkboxesDisabled: [false],
                title: [language.get(CustomiseRoleLanguage.EditOwnCMEProvisioning)],
                rowClassName: 'cme-edit-provisioning'
            },
            {
                name: RolePermissionsFieldNames.CRM_TEMPLATE_MANAGE,
                checkboxesDisabled: [false],
                title: [language.get(CustomiseRoleLanguage.CrmTemplateManage)],
                rowClassName: 'crm-template-manage'
            }
        ]
    }

    /**
     * Tab activation method.
     * @param newActiveTabID ID of tab to activate.
     */
    const activateTab = (newActiveTabID: number): void => {
        setActiveTabID(newActiveTabID)
        // TODO: Setup help page for CME modal.
        // setModalHelpTopicID(getHelpTopicByTab(newActiveTabID))
    }

    const tabs: Tab[] = [
        {
            ID: CustomiseRoleTabIDs.ADMINISTRATE,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(CustomiseRoleLanguage.TableHeadingAdminAccess),
                iconName: IconName.ADMIN,
                onClick: (): void => activateTab(CustomiseRoleTabIDs.ADMINISTRATE)
            },
            content: (
                <Table
                    {...tableDataCreator(
                        methods,
                        adminPermissionsLayout,
                        [ROLE_ACTIONS.READ, ROLE_ACTIONS.CREATE, ROLE_ACTIONS.UPDATE, ROLE_ACTIONS.DELETE],
                        userInfo?.Permissions?.Admin ?? []
                    )}
                />
            )
        },
        {
            ID: CustomiseRoleTabIDs.STATUS_TOOLS,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(CustomiseRoleLanguage.TableHeadingStatusTools),
                iconName: IconName.WRENCH,
                onClick: (): void => activateTab(CustomiseRoleTabIDs.STATUS_TOOLS)
            },
            content: (
                <Table
                    {...tableDataCreator(
                        methods,
                        statusToolsLayout,
                        [ROLE_ACTIONS.READ, ROLE_ACTIONS.UPDATE],
                        userInfo?.Permissions?.Admin ?? []
                    )}
                />
            )
        },
        {
            ID: CustomiseRoleTabIDs.ACTIONS,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(CustomiseRoleLanguage.TableHeadingActions),
                iconName: IconName.HOW_TO,
                onClick: (): void => activateTab(CustomiseRoleTabIDs.ACTIONS)
            },
            content: (
                <Table
                    {...tableDataCreator(
                        methods,
                        actionsLayout,
                        [ROLE_ACTIONS.ALLOWED],
                        userInfo?.Permissions?.Admin ?? []
                    )}
                />
            )
        }
    ]

    // eslint-disable-next-line
    const submitHandler = (state: Record<string, any>): void => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        onSubmit(state.administrator.rolepermissions as Record<string, Record<string, boolean>>)
        closeModal()
    }

    return (
        <FormProvider {...methods}>
            <form
                onSubmit={(event): void => {
                    event.preventDefault()
                    event.stopPropagation()

                    handleSubmit(submitHandler)(event)
                }}
            >
                <div className='modal-title-report-config'>
                    {language.get(CustomiseRoleLanguage.CustomisePopupTitle)}
                </div>

                <div className='customise-role-container'>
                    <Tabs tabs={tabs} activeTabID={activeTabID} />
                </div>

                <FooterActions closeModal={closeModal} />
            </form>
        </FormProvider>
    )
}
